/**
 * Individual blog post page with a lightweight Markdown renderer,
 * table-of-contents sidebar, social sharing, and related-posts section.
 *
 * @module BlogPostPage
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  Button,
  Divider,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip,
  alpha,
  Grid2 as Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { GREEN, DARK_BLUE } from '../theme';
import { PageTransition, FadeInSection, FloatingOrb } from '../components/AnimatedComponents';
import api from '../api';

/** Shape of a single translation within a blog post. */
interface BlogTranslation {
  title: string;
  excerpt: string;
  content: string;
  meta_description: string;
  meta_keywords: string;
}

/** Complete blog post record as returned by the API. */
interface BlogPost {
  id: string;
  slug: string;
  translations: Record<string, BlogTranslation>;
  author: string;
  category: string;
  tags: string[];
  cover_image: string;
  is_published: boolean;
  reading_time: number;
  created_at: string;
  updated_at: string;
}

/** Resolve the best available translation for a post, falling back to nl then en. */
function getTranslation(post: BlogPost, lang: string): BlogTranslation {
  return post.translations[lang] || post.translations['nl'] || post.translations['en'] || {
    title: '',
    excerpt: '',
    content: '',
    meta_description: '',
    meta_keywords: '',
  };
}

/**
 * Parse a subset of Markdown (headings, lists, blockquotes, code blocks,
 * images, horizontal rules) into React nodes for display.
 */
function renderContent(content: string): React.ReactNode[] {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listKey = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <Box
          component="ul"
          key={`list-${listKey++}`}
          sx={{ pl: 3, mb: 2, '& li': { mb: 0.5, lineHeight: 1.8 } }}
        >
          {listItems.map((item, i) => (
            <li key={i}>
              <Typography variant="body1" component="span">
                {renderInlineMarkdown(item)}
              </Typography>
            </li>
          ))}
        </Box>
      );
      listItems = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('### ')) {
      flushList();
      elements.push(
        <Typography
          key={i}
          variant="h5"
          component="h3"
          sx={{ fontWeight: 600, mt: 4, mb: 2 }}
          id={slugify(line.slice(4))}
        >
          {renderInlineMarkdown(line.slice(4))}
        </Typography>
      );
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(
        <Typography
          key={i}
          variant="h4"
          component="h2"
          sx={{ fontWeight: 700, mt: 5, mb: 2 }}
          id={slugify(line.slice(3))}
        >
          {renderInlineMarkdown(line.slice(3))}
        </Typography>
      );
    } else if (line.startsWith('# ')) {
      flushList();
      elements.push(
        <Typography
          key={i}
          variant="h3"
          component="h2"
          sx={{ fontWeight: 800, mt: 5, mb: 2 }}
          id={slugify(line.slice(2))}
        >
          {renderInlineMarkdown(line.slice(2))}
        </Typography>
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      listItems.push(line.slice(2));
    } else if (line.startsWith('> ')) {
      flushList();
      elements.push(
        <Box
          key={i}
          component="blockquote"
          sx={{
            borderLeft: `4px solid ${GREEN}`,
            pl: 3,
            py: 1,
            my: 3,
            bgcolor: alpha(GREEN, 0.04),
            borderRadius: '0 12px 12px 0',
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            {renderInlineMarkdown(line.slice(2))}
          </Typography>
        </Box>
      );
    } else if (line.startsWith('```')) {
      flushList();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <Box
          key={`code-${i}`}
          component="pre"
          sx={{
            bgcolor: DARK_BLUE,
            color: '#e0e0e0',
            p: 3,
            borderRadius: 3,
            overflow: 'auto',
            my: 3,
            fontSize: '0.9rem',
            lineHeight: 1.6,
          }}
        >
          <code>{codeLines.join('\n')}</code>
        </Box>
      );
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      elements.push(
        <Typography key={i} variant="body1" sx={{ mb: 2, lineHeight: 1.9, color: 'text.primary' }}>
          {renderInlineMarkdown(line)}
        </Typography>
      );
    }
  }
  flushList();
  return elements;
}

/** Render inline Markdown (bold, italic, links) within a text fragment. */
function renderInlineMarkdown(text: string): React.ReactNode {
  // Handle **bold** and *italic*
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const italicMatch = remaining.match(/\*(.+?)\*/);
    const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

    const matches = [
      boldMatch ? { type: 'bold', index: boldMatch.index!, match: boldMatch } : null,
      italicMatch && (!boldMatch || italicMatch.index! < boldMatch.index!) ? { type: 'italic', index: italicMatch.index!, match: italicMatch } : null,
      linkMatch ? { type: 'link', index: linkMatch.index!, match: linkMatch } : null,
    ].filter(Boolean).sort((a, b) => a!.index - b!.index);

    if (matches.length === 0) {
      parts.push(remaining);
      break;
    }

    const first = matches[0]!;
    if (first.index > 0) {
      parts.push(remaining.slice(0, first.index));
    }

    if (first.type === 'bold') {
      parts.push(<strong key={key++}>{first.match[1]}</strong>);
      remaining = remaining.slice(first.index + first.match[0].length);
    } else if (first.type === 'italic') {
      parts.push(<em key={key++}>{first.match[1]}</em>);
      remaining = remaining.slice(first.index + first.match[0].length);
    } else if (first.type === 'link') {
      parts.push(
        <a key={key++} href={first.match[2]} target="_blank" rel="noopener noreferrer" style={{ color: GREEN }}>
          {first.match[1]}
        </a>
      );
      remaining = remaining.slice(first.index + first.match[0].length);
    } else {
      parts.push(remaining);
      break;
    }
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

/** Convert a heading text to a URL-safe anchor id. */
function slugify(text: string): string {
  return text.toLowerCase().replace(/[^\w]+/g, '-').replace(/(^-|-$)/g, '');
}

/** Extract all h1–h3 headings from Markdown content for a table of contents. */
function extractHeadings(content: string): { level: number; text: string; id: string }[] {
  return content
    .split('\n')
    .filter((line) => /^#{1,3}\s/.test(line))
    .map((line) => {
      const match = line.match(/^(#{1,3})\s(.+)/);
      if (!match) return null;
      return {
        level: match[1].length,
        text: match[2],
        id: slugify(match[2]),
      };
    })
    .filter(Boolean) as { level: number; text: string; id: string }[];
}

/** Blog post detail page with Markdown rendering, TOC, and sharing buttons. */
export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'nl';

  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api
      .get(`/api/blog/${slug}`)
      .then((res) => {
        setPost(res.data);
        setNotFound(false);
        // Fetch related posts
        return api.get('/api/blog/', { params: { published_only: true } });
      })
      .then((res) => {
        if (res) {
          const all: BlogPost[] = res.data;
          setRelatedPosts(all.filter((p) => p.slug !== slug).slice(0, 3));
        }
      })
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    if (navigator.share && post) {
      const tr = getTranslation(post, lang);
      navigator.share({
        title: tr.title,
        text: tr.excerpt,
        url: window.location.href,
      });
    }
  };

  if (loading) {
    return (
      <PageTransition>
        <Container maxWidth="md" sx={{ py: 12, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('admin.loading')}</Typography>
        </Container>
      </PageTransition>
    );
  }

  if (notFound || !post) {
    return (
      <PageTransition>
        <Container maxWidth="md" sx={{ py: 12, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 2 }}>404</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>{t('blog.notFound')}</Typography>
          <Button component={RouterLink} to="/blog" variant="contained" color="secondary" sx={{ borderRadius: 25 }}>
            {t('blog.backToBlog')}
          </Button>
        </Container>
      </PageTransition>
    );
  }

  const tr = getTranslation(post, lang);
  const headings = extractHeadings(tr.content);

  const postUrl = `https://www.booge-it.com/blog/${post.slug}`;

  // JSON-LD BlogPosting structured data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: tr.title,
    description: tr.meta_description || tr.excerpt,
    image: post.cover_image || undefined,
    author: {
      '@type': 'Person',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Booge IT',
      url: 'https://www.booge-it.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.booge-it.com/og-image.png',
      },
    },
    datePublished: post.created_at,
    dateModified: post.updated_at,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl,
    },
    url: postUrl,
    wordCount: tr.content.split(/\s+/).length,
    timeRequired: `PT${post.reading_time}M`,
    keywords: tr.meta_keywords || post.tags.join(', '),
    articleSection: post.category || undefined,
    inLanguage: lang,
  };

  // Breadcrumb structured data
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('nav.home'), item: 'https://www.booge-it.com/' },
      { '@type': 'ListItem', position: 2, name: t('blog.title'), item: 'https://www.booge-it.com/blog' },
      { '@type': 'ListItem', position: 3, name: tr.title, item: postUrl },
    ],
  };

  // hreflang alternatives
  const availableLangs = Object.keys(post.translations);
  const hreflangMap: Record<string, string> = {};
  availableLangs.forEach((l) => {
    hreflangMap[l] = `/blog/${post.slug}`;
  });

  return (
    <PageTransition>
      <SEO
        title={tr.title}
        description={tr.meta_description || tr.excerpt}
        canonical={`/blog/${post.slug}`}
        type="article"
        image={post.cover_image || undefined}
        jsonLd={jsonLd}
        keywords={tr.meta_keywords || post.tags.join(', ')}
        hreflangMap={hreflangMap}
      />
      {/* Additional structured data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>

      {/* Hero / Cover Image */}
      {post.cover_image && (
        <Box
          sx={{
            width: '100%',
            height: { xs: 250, md: 400 },
            backgroundImage: `url(${post.cover_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '50%',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.4))',
            },
          }}
        />
      )}

      <Box
        component="article"
        itemScope
        itemType="https://schema.org/BlogPosting"
        sx={{
          py: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Main content */}
            <Grid size={{ xs: 12, md: headings.length > 0 ? 8 : 12 }}>
              {/* Breadcrumb */}
              <FadeInSection>
                <nav aria-label="breadcrumb">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                    <Button
                      component={RouterLink}
                      to="/blog"
                      startIcon={<ArrowBackIcon />}
                      size="small"
                      sx={{ borderRadius: 25, color: 'text.secondary' }}
                    >
                      {t('blog.backToBlog')}
                    </Button>
                  </Stack>
                </nav>
              </FadeInSection>

              {/* Post header */}
              <FadeInSection delay={0.1}>
                <Box component="header" sx={{ mb: 4 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    {post.category && (
                      <Chip
                        label={post.category}
                        size="small"
                        sx={{
                          bgcolor: alpha(GREEN, 0.1),
                          color: GREEN,
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Stack>
                  <Typography
                    variant="h2"
                    component="h1"
                    itemProp="headline"
                    sx={{
                      fontWeight: 800,
                      mb: 3,
                      lineHeight: 1.2,
                      background: `linear-gradient(135deg, ${DARK_BLUE}, ${alpha(DARK_BLUE, 0.8)})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {tr.title}
                  </Typography>

                  {/* Meta info */}
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 2 }}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" itemProp="author">
                        {post.author}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        <time dateTime={post.created_at} itemProp="datePublished">
                          {t('blog.publishedOn', {
                            date: new Date(post.created_at).toLocaleDateString(lang, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            }),
                          })}
                        </time>
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {t('blog.readingTime', { minutes: post.reading_time })}
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Share buttons */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {t('blog.sharePost')}:
                    </Typography>
                    <Tooltip title={copied ? t('blog.copied') : t('blog.copyLink')}>
                      <IconButton size="small" onClick={handleCopyLink}>
                        <LinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {typeof navigator.share === 'function' && (
                      <Tooltip title={t('blog.share')}>
                        <IconButton size="small" onClick={handleShare}>
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              </FadeInSection>

              <Divider sx={{ mb: 4 }} />

              {/* Article body */}
              <FadeInSection delay={0.2}>
                <Box
                  itemProp="articleBody"
                  sx={{
                    '& h2': { scrollMarginTop: '80px' },
                    '& h3': { scrollMarginTop: '80px' },
                    '& a': { color: GREEN, textDecoration: 'underline' },
                  }}
                >
                  {renderContent(tr.content)}
                </Box>
              </FadeInSection>

              {/* Tags */}
              {post.tags.length > 0 && (
                <FadeInSection delay={0.3}>
                  <Divider sx={{ my: 4 }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('blog.tags')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {post.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          component={RouterLink}
                          to={`/blog?tag=${encodeURIComponent(tag)}`}
                          clickable
                          sx={{ fontWeight: 500 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </FadeInSection>
              )}

              {/* hreflang - hidden meta for SEO */}
              <Box sx={{ display: 'none' }}>
                {availableLangs.map((l) => (
                  <link key={l} rel="alternate" hrefLang={l} href={`https://www.booge-it.com/${l}/blog/${post.slug}`} />
                ))}
              </Box>
            </Grid>

            {/* Sidebar - Table of Contents */}
            {headings.length > 0 && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Box
                  component="aside"
                  sx={{
                    position: 'sticky',
                    top: 100,
                    p: 3,
                    bgcolor: alpha(DARK_BLUE, 0.02),
                    borderRadius: 4,
                    border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    {t('blog.tableOfContents')}
                  </Typography>
                  <Box component="nav" aria-label="Table of contents">
                    {headings.map((heading, idx) => (
                      <Typography
                        key={idx}
                        component="a"
                        href={`#${heading.id}`}
                        variant="body2"
                        sx={{
                          display: 'block',
                          pl: (heading.level - 1) * 2,
                          py: 0.5,
                          color: 'text.secondary',
                          textDecoration: 'none',
                          borderLeft: `2px solid transparent`,
                          transition: 'all 0.2s',
                          '&:hover': {
                            color: GREEN,
                            borderLeftColor: GREEN,
                            pl: (heading.level - 1) * 2 + 0.5,
                          },
                        }}
                      >
                        {heading.text}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Related posts */}
          {relatedPosts.length > 0 && (
            <FadeInSection delay={0.4}>
              <Divider sx={{ my: 6 }} />
              <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 4 }}>
                {t('blog.relatedPosts')}
              </Typography>
              <Grid container spacing={3}>
                {relatedPosts.map((related) => {
                  const relTr = getTranslation(related, lang);
                  return (
                    <Grid key={related.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card
                        component="article"
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: `0 6px 20px ${alpha(DARK_BLUE, 0.08)}`,
                          },
                        }}
                      >
                        {related.cover_image && (
                          <CardMedia
                            component="img"
                            image={related.cover_image}
                            alt={relTr.title}
                            sx={{ height: 160, objectFit: 'cover' }}
                          />
                        )}
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                            <RouterLink
                              to={`/blog/${related.slug}`}
                              style={{ color: 'inherit', textDecoration: 'none' }}
                            >
                              {relTr.title}
                            </RouterLink>
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {relTr.excerpt}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </FadeInSection>
          )}
        </Container>
      </Box>
    </PageTransition>
  );
}
